// Import statements remain the same in ES6
import { GOOGLE_API, GOOGLE_STATIC } from 'app-config/index.js'

// Enums are replaced with objects
const MAPTYPE = {
    hybrid: 'hybrid',
    road: 'road',
    satellite: 'satellite',
    terrain: 'terrain',
}

const IMAGEFORMAT = {
    png: 'png',
    png8: 'png',
    gif: 'gif',
    jpg: 'jpg',
    jpeg: 'jpg',
    // Non-progressive JPEG — avoids Firefox "Image corrupt or truncated" on Google's
    // progressive `format=jpg` responses (especially while the image is still streaming).
    'jpg-baseline': 'jpg-baseline',
    'jpeg-baseline': 'jpg-baseline',
}

// Default parameters are defined as a simple object
const ImageParamDefaults = {
    format: IMAGEFORMAT.png,
    geodesic: false,
    maptype: MAPTYPE.satellite,
    scale: 1,
    size: '100x100',
}

// Functions remain mostly unchanged, but without type annotations
export function staticMap(p) {
    return urlAppend({ ...ImageParamDefaults, ...p }).href
}

// // Export statement remains the same in ES6
// export { staticMap }

function urlAppend(mapParams) {
    const gmsiURL = new URL(GOOGLE_STATIC)
    var sp = gmsiURL.searchParams
    // Paddock polygons go underneath the asset and livestock markers.
    if (Array.isArray(mapParams.paths)) mapParams.paths.forEach(({ path, pathParams }) => sp.append('path', makePathV2(path, pathParams)))
    Object.keys(mapParams).forEach(key => {
        switch (key) {
            case 'center':
            case 'color':
            case 'format':
            case 'maptype':
            case 'region':
            case 'scale':
            case 'size':
            case 'zoom':
                sp.append(key, mapParams[key])
                break
            case 'style':
                if (Array.isArray(mapParams.style)) {
                    mapParams.style.forEach(s => {
                        sp.append('style', s)
                    })
                }
                break
            case 'markers':
                if (Array.isArray(mapParams.markers)) {
                    mapParams.markers.forEach(s => {
                        sp.append('markers', s)
                    })
                }
                break
            case 'weight':
                sp.append('weight', mapParams[key].toString())
                break
            default:
            // Other parameters can be added here if needed
        }
    })
    if (mapParams.path) {
        sp.append('path', makePathV2(mapParams.path, mapParams.pathParams))
        // Extract zoom from pathParams if present (zoom should be at map level, not path level)
        if (mapParams.pathParams?.zoom != null && mapParams.zoom == null) {
            sp.append('zoom', mapParams.pathParams.zoom)
        }
    }
    sp.append('key', GOOGLE_API)
    return gmsiURL
}

// Set weight to a value >= 1 and an integer as per the Google Static Maps API documentation
function makePathV2(
    points,
    {
        color = '0xFF0000FF', // stroke RGBA
        fill = '0xFF000033', // fill RGBA
        weight = 1,
        geodesic = true,
        close = true,
    } = {},
) {
    // Floor to an integer and clamp to >= 1 (API requires weight ≥ 1). Was Math.min(..., 1)
    // which incorrectly forced every stroke to weight 1.
    weight = Math.max(1, parseInt(Number(weight), 10) || 1)
    const enc = polylineEncode(pathToArray(points, { close }))
    const parts = [fill ? `fillcolor:${fill}` : null, color ? `color:${color}` : null, `weight:${weight}`, geodesic ? 'geodesic:true' : null, `enc:${enc}`].filter(Boolean)
    return parts.join('|') // DON'T pre-encode; URLSearchParams will
}

// function makePath(path, geodesic) {
//     let pathStyle = []
//     path.forEach(point => {
//         let part = `weight:${point.weight || ImageParamDefaults.weight}`
//         if (point.color) {
//             part += `|color:${point.color}`
//         }
//         if (geodesic) {
//             part += `|geodesic:${geodesic}`
//         }
//         // Add other style properties as needed
//         pathStyle.push(part)
//     })
//     // Encode path array to a polyline
//     return polylineEncode(pathToArray(path))
// }

function pathToArray(path, { close = true, eps = 1e-6 } = {}) {
    const arr = path.map(p => [p.lat ?? p.latitude, p.lng ?? p.longitude]).filter(([a, b]) => a != null && b != null)
    if (close && arr.length > 2) {
        const [fLat, fLng] = arr[0],
            [lLat, lLng] = arr[arr.length - 1]
        if (Math.abs(fLat - lLat) > eps || Math.abs(fLng - lLng) > eps) arr.push(arr[0]) // close polygon
    }
    return arr
}

function polylineEncode(coordinates, precision = 5) {
    const factor = Math.pow(10, precision)
    let output = coordinates.reduce((acc, point, index) => {
        if (index === 0) {
            return encode(point[0], 0, factor) + encode(point[1], 0, factor)
        } else {
            const previous = coordinates[index - 1]
            return acc + encode(point[0], previous[0], factor) + encode(point[1], previous[1], factor)
        }
    }, '')
    return output
}

function encode(current, previous, factor) {
    current = py2_round(current * factor)
    previous = py2_round(previous * factor)
    let coordinate = current - previous
    coordinate <<= 1
    if (current - previous < 0) {
        coordinate = ~coordinate
    }
    let output = ''
    while (coordinate >= 0x20) {
        output += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63)
        coordinate >>= 5
    }
    output += String.fromCharCode(coordinate + 63)
    return output
}

function py2_round(value) {
    return value < 0 ? Math.ceil(value - 0.5) : Math.floor(value + 0.5)
}

// The encode and py2_round functions would remain the same as in the original code

// Note that the original 'makePath' and 'polylineEncode' functions are not fully defined here,
// so you would need to implement the logic for encoding the polyline as per your requirements.
